import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Logo from "../../components/Logo";
import api from "../../utils/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Auth.css";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("resetPassword.invalidToken"));
    }
  }, [token, t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!formData.password) {
      setError(t("resetPassword.passwordRequired"));
      return;
    }

    if (formData.password.length < 6) {
      setError(t("resetPassword.passwordMinLength"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("resetPassword.passwordsMismatch"));
      return;
    }

    if (!token) {
      setError(t("resetPassword.invalidToken"));
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`/auth/reset-password/${token}`, {
        password: formData.password,
      });

      setLoading(false);
      setSuccess(response.data.message || t("resetPassword.successMessage"));

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      setLoading(false);
      const errorMessage =
        error.response?.data?.message || t("resetPassword.expiredToken");
      setError(errorMessage);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Link to="/">
          <Logo />
        </Link>

        <div className="auth-card-inner">
          <h3 className="auth-card-title">{t("resetPassword.title")}</h3>
          <p className="auth-subtitle">{t("resetPassword.subtitle")}</p>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error-message">{error}</div>}

            {success && <div className="auth-success-message">{success}</div>}

            <div className="auth-form-group">
              <label htmlFor="password">{t("resetPassword.newPassword")}</label>
              <div className="auth-password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`auth-form-input ${error && !success ? "error" : ""}`}
                  placeholder={t("resetPassword.newPasswordPlaceholder")}
                  autoComplete="new-password"
                  disabled={loading || !!success}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? t("login.hidePassword")
                      : t("login.showPassword")
                  }
                >
                  {showPassword ? (
                    <FaEyeSlash aria-hidden />
                  ) : (
                    <FaEye aria-hidden />
                  )}
                </button>
              </div>
              <small style={{ color: "#666", fontSize: "12px" }}>
                {t("resetPassword.passwordHelp")}
              </small>
            </div>

            <div className="auth-form-group">
              <label htmlFor="confirmPassword">
                {t("resetPassword.confirmPassword")}
              </label>
              <div className="auth-password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`auth-form-input ${error && !success ? "error" : ""}`}
                  placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                  autoComplete="new-password"
                  disabled={loading || !!success}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword
                      ? t("login.hidePassword")
                      : t("login.showPassword")
                  }
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash aria-hidden />
                  ) : (
                    <FaEye aria-hidden />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-btn-primary"
              disabled={loading || !!success}
            >
              {loading
                ? t("resetPassword.resetting")
                : success
                  ? t("resetPassword.passwordReset")
                  : t("resetPassword.resetPassword")}
            </button>

            <div className="auth-footer">
              <span>{t("resetPassword.rememberPassword")} </span>
              <Link to="/login" className="auth-link">
                {t("resetPassword.backToLogin")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
